// Card validation pipeline (§14.6), play resolution, cooldown/use tracking (§14.5).

import { buildingDef, cardDef, defs, ritualDef, spellDef, trapDef, unitDef } from '../../content/registry';
import { TUNING } from '../../content/tuning';
import type { CardDef, PlayerId } from '../../content/types';
import { dist, vec, type Vec2 } from '../../shared/vec';
import type { SimCommand } from '../commands';
import type { Sim } from '../sim';
import type { CardUiState } from '../state';
import { maybeEchoSquad } from './annexes';
import { nearConnectedConduit } from './network';
import { anyValidRitualLocation, startRitual, validRitualPos } from './rituals';

const dt = TUNING.dt;

export type PlayDenyReason =
  | 'ok'
  | 'noUses'
  | 'cooldown'
  | 'activeLimit'
  | 'wrongAscension'
  | 'insufficientPower'
  | 'insufficientEssence'
  | 'insufficientCommand'
  | 'missingPrerequisite'
  | 'noLocation'
  | 'invalidTarget';

/** Non-target checks (§14.6 chain). Used by UI states, AI, and resolution. */
export function canPlayBase(sim: Sim, player: PlayerId, cardId: string): PlayDenyReason {
  const p = sim.state.players[player];
  const cr = p.cards[cardId];
  const def = cardDef(cardId);
  if (!cr) return 'noUses';
  if (cr.usesRemaining <= 0) return 'noUses';
  if (cr.cooldownRemaining > 0) return 'cooldown';
  if (def.maxActive !== undefined && cr.activeCopies >= def.maxActive) return 'activeLimit';
  if (p.ascensionPlayable < def.ascension) return 'wrongAscension';
  if (p.power < def.cost.power) return 'insufficientPower';
  if (p.essence < def.cost.essence) return 'insufficientEssence';
  const cmd = def.cost.command ?? 0;
  if (cmd > 0 && p.commandUsed + cmd > p.commandCap) return 'insufficientCommand';
  // Unit cards are self-contained summons once they are in the player's deck.
  // Building requirements are reserved for non-unit cards such as structure or
  // infrastructure unlocks; production buildings still control their own rosters.
  if (def.category !== 'unit' && def.requiresBuildings) {
    const siteAlternative = !!def.requiresBuildings.orControlledSite && sim.state.sites.some((site) => site.owner === player);
    const owns = (id: string): boolean => {
      for (const b of sim.state.buildings.values()) {
        if (b.owner !== player || b.state !== 'active' || b.defId !== id) continue;
        if (!def.requiresBuildings?.connected || sim.state.connectedConduits.has(b.id)) return true;
      }
      return false;
    };
    if (!siteAlternative && def.requiresBuildings.any && !def.requiresBuildings.any.some(owns)) return 'missingPrerequisite';
    if (!siteAlternative && def.requiresBuildings.all && !def.requiresBuildings.all.every(owns)) return 'missingPrerequisite';
  }
  // any valid location at all?
  if (!anyValidLocation(sim, player, def)) return 'noLocation';
  return 'ok';
}

function anyValidLocation(sim: Sim, player: PlayerId, def: CardDef): boolean {
  if (def.category === 'ritual') {
    return anyValidRitualLocation(sim, player, ritualDef(def.subjectId));
  }
  if (def.summonLocations.includes('anywhere-friendly')) {
    if (def.category === 'spell') {
      const sd = spellDef(def.subjectId);
      if (sd.targeting.shape === 'entity') {
        if (sd.targeting.validTargets !== 'friendly-building') {
          for (const s of sim.state.squads.values()) if (s.owner === player) return true;
        }
        for (const b of sim.state.buildings.values()) {
          if (b.owner === player && b.state !== 'destroyed') return true;
        }
        return false;
      }
    }
    return true;
  }
  if (def.summonLocations.includes('core') && sim.coreAlive(player)) return true;
  if (def.summonLocations.includes('controlled-site')) {
    for (const s of sim.state.sites) if (s.owner === player) return true;
  }
  return false;
}

export function cardUiState(sim: Sim, player: PlayerId, cardId: string): CardUiState {
  const r = canPlayBase(sim, player, cardId);
  switch (r) {
    case 'ok':
      return 'available';
    case 'noUses':
      return 'noUses';
    case 'cooldown':
      return 'cooldown';
    case 'activeLimit':
      return 'activeLimit';
    case 'wrongAscension':
      return 'wrongAscension';
    case 'insufficientCommand':
      return 'insufficientCommand';
    case 'insufficientEssence':
      return 'insufficientEssence';
    case 'insufficientPower':
      return 'insufficientPower';
    case 'missingPrerequisite':
      return 'noLocation';
    default:
      return 'noLocation';
  }
}

export interface PlacementCheck {
  ok: boolean;
  reason: string;
  siteId: string | null;
}

/** Building/trap placement validation (§15.1). */
export function validatePlacement(
  sim: Sim,
  player: PlayerId,
  footprint: { w: number; h: number },
  pos: Vec2,
  isMajor: boolean,
): PlacementCheck {
  // zone: own core territory or owned site build radius
  let siteId: string | null = null;
  let inZone = false;
  const core = sim.coreAlive(player);
  if (core && dist(pos, core.pos) <= sim.map.coreTerritoryRadius) inZone = true;
  for (const s of sim.state.sites) {
    if (s.owner === player && dist(pos, s.pos) <= TUNING.buildRadius) {
      inZone = true;
      siteId = s.id;
      break;
    }
  }
  if (!inZone) return { ok: false, reason: 'Must build in Core territory or at a controlled site', siteId: null };

  // footprint free
  for (let dx = -footprint.w / 2; dx < footprint.w / 2; dx += 1) {
    for (let dy = -footprint.h / 2; dy < footprint.h / 2; dy += 1) {
      if (!sim.nav.isWalkable(vec(pos.x + dx + 0.5, pos.y + dy + 0.5))) {
        return { ok: false, reason: 'Blocked terrain', siteId };
      }
    }
  }
  // wall foundations reserve their footprint — a wall may rise there later (unbuilt walls
  // are deliberately absent from the nav grid, so the walkability check above misses them)
  for (const r of sim.state.ruins) {
    if (r.buildingId !== null) continue; // a standing wall is stamped in nav already
    const rw = (r.rot === 1 ? 12 : 2) / 2 + footprint.w / 2; // all faction walls are 2×12
    const rh = (r.rot === 1 ? 2 : 12) / 2 + footprint.h / 2;
    if (Math.abs(pos.x - r.pos.x) < rw && Math.abs(pos.y - r.pos.y) < rh)
      return { ok: false, reason: 'Blocked by wall foundations', siteId };
  }
  // min 4 m from core footprint, 10 m from enemy structures
  for (const b of sim.state.buildings.values()) {
    const bd = buildingDef(b.defId);
    const clearance = (Math.max(bd.footprint.w, bd.footprint.h) + Math.max(footprint.w, footprint.h)) / 2;
    if (b.isCore && dist(pos, b.pos) < clearance + 4) return { ok: false, reason: 'Too close to a Core', siteId };
    if (b.owner !== player && dist(pos, b.pos) < clearance + 10)
      return { ok: false, reason: 'Too close to enemy structures', siteId };
    if (dist(pos, b.pos) < clearance + 0.5) return { ok: false, reason: 'Overlaps a structure', siteId };
  }
  // major structure limits
  if (isMajor) {
    if (siteId) {
      let count = 0;
      for (const b of sim.state.buildings.values()) {
        if (b.owner === player && b.siteId === siteId && buildingDef(b.defId).isMajorStructure) count++;
      }
      if (count >= TUNING.majorStructuresPerSite)
        return { ok: false, reason: `Site structure limit (${TUNING.majorStructuresPerSite}) reached`, siteId };
    } else {
      let count = 0;
      for (const b of sim.state.buildings.values()) {
        if (b.owner === player && b.siteId === null && !b.isCore && buildingDef(b.defId).isMajorStructure) count++;
      }
      if (count >= TUNING.coreTerritoryDeckBuildings)
        return { ok: false, reason: `Core territory building limit (${TUNING.coreTerritoryDeckBuildings}) reached`, siteId };
    }
  }
  return { ok: true, reason: '', siteId };
}

export function processPlayCard(sim: Sim, cmd: Extract<SimCommand, { kind: 'PlayCard' }>): void {
  const { player, cardId, targetPos, targetEntity } = cmd;
  const p = sim.state.players[player];
  const def = cardDef(cardId);
  const base = canPlayBase(sim, player, cardId);
  if (base !== 'ok') {
    sim.pushEvent({ type: 'CardDenied', player, cardId, reason: base });
    return;
  }

  const cr = p.cards[cardId];
  const commit = () => {
    p.power -= def.cost.power;
    p.essence -= def.cost.essence;
    if (def.useModel.kind !== 'reusable') cr.usesRemaining -= 1;
    if (def.useModel.kind === 'reusable') cr.cooldownRemaining = def.useModel.cooldown;
    else if (def.useModel.kind === 'limited') cr.cooldownRemaining = def.useModel.cooldown;
    p.stats.cardsPlayed++;
    sim.pushEvent({ type: 'CardPlayed', player, cardId });
  };

  switch (def.category) {
    case 'unit':
    case 'boss': {
      if (!sim.isValidSummonPos(targetPos, player, def.summonLocations, def.ascension)) {
        sim.pushEvent({ type: 'CardDenied', player, cardId, reason: 'invalidTarget' });
        return;
      }
      const extrasPre = def.category === 'boss' ? defs.bossExtras[def.subjectId] : undefined;
      // Gravebound Colossus: assembled by destroying a friendly non-boss squad nearby
      if (extrasPre?.summonSacrifice) {
        let victim = null as import('../state').Squad | null;
        let bestD = 9;
        for (const s of sim.state.squads.values()) {
          if (s.owner !== player || s.isBoss) continue;
          const d = dist(s.pos, targetPos);
          if (d < bestD) {
            bestD = d;
            victim = s;
          }
        }
        if (!victim) {
          sim.pushEvent({ type: 'CardDenied', player, cardId, reason: 'invalidTarget' });
          return;
        }
        sim.killSquad(victim);
      }
      commit();
      // Devoured Mass (Devouring Gate): 12% boss Power discount per stored sacrifice
      if (def.category === 'boss' && p.devouredMass > 0) {
        p.power += Math.round(def.cost.power * 0.12 * p.devouredMass);
        p.devouredMass = 0;
      }
      const extras = def.category === 'boss' ? defs.bossExtras[def.subjectId] : undefined;
      if (extras && extras.summonSeconds > 0) {
        // announced assembly sequence (§20.1): the boss arrives after summonSeconds
        sim.state.pendingSummons.push({
          defId: def.subjectId,
          cardId,
          owner: player,
          pos: { ...targetPos },
          remaining: extras.summonSeconds,
          total: extras.summonSeconds,
        });
        sim.pushEvent({
          type: 'BossSummoning',
          player,
          defId: def.subjectId,
          pos: { ...targetPos },
          seconds: extras.summonSeconds,
          global: extras.announcement === 'global',
        });
        break;
      }
      const squad = sim.spawnSquad(def.subjectId, player, targetPos);
      if (def.category === 'boss') {
        sim.pushEvent({ type: 'BossSummoned', player, defId: def.subjectId, pos: { ...squad.pos } });
      } else {
        // Echo Observatory: duplicate a squad summoned at a connected location
        maybeEchoSquad(sim, player, def.subjectId, squad.pos);
      }
      break;
    }
    case 'building': {
      const bd = buildingDef(def.subjectId);
      const check = validatePlacement(sim, player, bd.footprint, targetPos, bd.isMajorStructure);
      if (!check.ok) {
        sim.pushEvent({ type: 'CardDenied', player, cardId, reason: check.reason });
        return;
      }
      commit();
      cr.activeCopies++;
      sim.spawnBuilding(def.subjectId, player, targetPos, { siteId: check.siteId, cardId });
      break;
    }
    case 'spell': {
      const sd = spellDef(def.subjectId);
      // Seal of Denial (AD-21): an enemy seal covering the target point cancels the
      // spell (40% Power refund); ultimates shatter the seal but still resolve
      for (const m of [...sim.state.mines.values()]) {
        if (!sim.areHostile(m.owner, player) || !m.built) continue;
        const mtd = trapDef(m.defId);
        if (mtd.behavior !== 'spellSeal') continue;
        if (dist(m.pos, targetPos) > mtd.triggerRadius) continue;
        sim.state.mines.delete(m.id);
        sim.pushEvent({ type: 'TrapTriggered', pos: { ...m.pos } });
        if (def.useModel.kind !== 'once') {
          commit();
          p.power += Math.round(def.cost.power * 0.4);
          return;
        }
        break;
      }
      if (sd.controlLock && sd.targeting.shape === 'entity') {
        // Phase Lock: enemy squad control
        const target = targetEntity !== undefined ? sim.state.squads.get(targetEntity) : undefined;
        if (!target || !sim.areHostile(target.owner, player)) {
          sim.pushEvent({ type: 'CardDenied', player, cardId, reason: 'invalidTarget' });
          return;
        }
        commit();
        const td = unitDef(target.defId);
        const lock = sd.controlLock;
        if (td.sizeTag === 'small' || td.sizeTag === 'medium') {
          target.rootedRemaining = Math.max(target.rootedRemaining, lock.rootSeconds);
          target.buffs.push({ stat: 'damageTaken', mult: 0.75, remaining: lock.rootSeconds });
        } else if (td.sizeTag === 'large') {
          target.buffs.push({ stat: 'moveSpeed', mult: 1 - lock.largeSlow, remaining: lock.slowSeconds });
        } else {
          target.buffs.push({ stat: 'moveSpeed', mult: 1 - lock.giantSlow, remaining: 3 });
        }
        break;
      }
      if (sd.targeting.shape === 'entity') {
        const b = targetEntity !== undefined ? sim.state.buildings.get(targetEntity) : undefined;
        const sq = targetEntity !== undefined ? sim.state.squads.get(targetEntity) : undefined;
        const wantsSquads = sd.targeting.validTargets !== 'friendly-building';
        // Reckoning: sacrifice a friendly squad, blasting enemies around it
        if (sd.sacrificeBlast && sq && sq.owner === player && !sq.isBoss) {
          commit();
          const blast = sq.maxHealth * sd.sacrificeBlast.fraction;
          const at = { ...sq.pos };
          sim.killSquad(sq);
          for (const e of [...sim.state.squads.values()]) {
            if (!sim.areHostile(e.owner, player)) continue;
            if (dist(e.pos, at) <= sd.sacrificeBlast.radius) {
              sim.damageSquad(e, blast, { isRanged: false, attackerOwner: player });
            }
          }
          sim.pushEvent({ type: 'TrapTriggered', pos: at });
          break;
        }
        // entity restoration (Regrowth): squads by fraction, structures by amount
        if (sd.entityHeal && wantsSquads && sq && sim.areAllies(sq.owner, player)) {
          commit();
          const frac =
            unitDef(sq.defId).sizeTag === 'giant' ? sd.entityHeal.giantFraction : sd.entityHeal.squadFraction;
          sq.health = Math.min(sq.maxHealth, sq.health + sq.maxHealth * frac * sq.healingTakenMult);
          break;
        }
        if (sd.sacrificeBlast) {
          // sacrifice spells never fall through to the building branch
          sim.pushEvent({ type: 'CardDenied', player, cardId, reason: 'invalidTarget' });
          return;
        }
        if (!b || !sim.areAllies(b.owner, player) || b.state === 'destroyed') {
          sim.pushEvent({ type: 'CardDenied', player, cardId, reason: 'invalidTarget' });
          return;
        }
        commit();
        if (sd.entityHeal) {
          const over = sd.entityHeal.overSeconds;
          b.healOverTime.push({ remaining: over, perSecond: sd.entityHeal.buildingAmount / over });
        }
        for (const eff of sd.effects) {
          if (eff.fx === 'heal') {
            const over = eff.overSeconds ?? 1;
            b.healOverTime.push({ remaining: over, perSecond: eff.amount / over });
          } else if (eff.fx === 'tempHealth') {
            b.tempHealth = Math.max(b.tempHealth, eff.amount);
            b.tempHealthRemaining = eff.duration;
          }
        }
      } else if (sd.targeting.shape === 'lane') {
        commit();
        // lane extends from the caster's core toward the click (assault corridor)
        const core = sim.coreAlive(player);
        const from = core ? core.pos : targetPos;
        const len = Math.max(0.001, dist(from, targetPos));
        const dir = vec((targetPos.x - from.x) / len, (targetPos.y - from.y) / len);
        const id = sim.newId();
        sim.state.zones.set(id, {
          id,
          owner: player,
          spellId: sd.id,
          pos: { ...targetPos },
          radius: sd.targeting.width / 2,
          remaining: (sd.laneAssault?.telegraphSeconds ?? 3) + 2,
          telegraphRemaining: sd.laneAssault?.telegraphSeconds ?? 3,
          lane: { dir, length: sd.targeting.length, width: sd.targeting.width },
          laneResolved: false,
        });
      } else if (sd.massTeleport) {
        // Emergency Translation (AD-19): destination = Core, controlled site, or a
        // linked conduit. Instant resolution (5 s telegraph simplified out — deviation);
        // translates the highest-Command non-Giant squads.
        const validDest =
          sim.isValidSummonPos(targetPos, player, ['core', 'controlled-site']) ||
          nearConnectedConduit(sim, player, targetPos, 12);
        if (!validDest) {
          sim.pushEvent({ type: 'CardDenied', player, cardId, reason: 'invalidTarget' });
          return;
        }
        commit();
        const spec = sd.massTeleport;
        const candidates = [...sim.state.squads.values()]
          .filter((s) => s.owner === player && unitDef(s.defId).sizeTag !== 'giant' && s.mountedOnSquad === null && s.mountedOnWall === null)
          .sort((a, b) => unitDef(b.defId).command - unitDef(a.defId).command || a.id - b.id)
          .slice(0, spec.maxSquads);
        let i = 0;
        for (const s of candidates) {
          const angle = (i++ / Math.max(1, candidates.length)) * Math.PI * 2;
          sim.teleportSquad(
            s,
            vec(targetPos.x + Math.cos(angle) * 3.5, targetPos.y + Math.sin(angle) * 3.5),
            10000,
          );
          s.tempHealth = Math.max(s.tempHealth, spec.shield);
          s.tempHealthRemaining = spec.shieldSeconds;
        }
      } else if (sd.temporarySummon) {
        // Last Battalion: temporary squads at a valid summon location
        if (!sim.isValidSummonPos(targetPos, player, def.summonLocations, def.ascension)) {
          sim.pushEvent({ type: 'CardDenied', player, cardId, reason: 'invalidTarget' });
          return;
        }
        commit();
        for (const unitId of sd.temporarySummon.units) {
          sim.spawnSquad(unitId, player, targetPos, { temporarySeconds: sd.temporarySummon.duration });
        }
      } else if (sd.targeting.shape === 'circle') {
        commit();
        const id = sim.newId();
        sim.state.zones.set(id, {
          id,
          owner: player,
          spellId: sd.id,
          pos: { ...targetPos },
          radius: sd.targeting.radius,
          remaining: sd.zoneDuration ?? 10,
          ...(sd.bombardment
            ? {
                telegraphRemaining: sd.bombardment.telegraphSeconds,
                impactsRemaining: sd.bombardment.impacts,
                impactTimer: 0,
              }
            : {}),
        });
      }
      break;
    }
    case 'ritual': {
      const rd = ritualDef(def.subjectId);
      if (!validRitualPos(sim, player, rd, targetPos)) {
        sim.pushEvent({ type: 'CardDenied', player, cardId, reason: 'invalidTarget' });
        return;
      }
      const check = validatePlacement(sim, player, { w: 2, h: 2 }, targetPos, false);
      if (!check.ok) {
        sim.pushEvent({ type: 'CardDenied', player, cardId, reason: check.reason });
        return;
      }
      commit();
      startRitual(sim, player, cardId, targetPos);
      break;
    }
    case 'trap': {
      const td = trapDef(def.subjectId);
      const check = validatePlacement(sim, player, { w: 1, h: 1 }, targetPos, false);
      if (!check.ok) {
        sim.pushEvent({ type: 'CardDenied', player, cardId, reason: check.reason });
        return;
      }
      commit();
      cr.activeCopies++;
      const fieldId = sim.state.nextFieldId++;
      for (let i = 0; i < td.perDeploymentPieces; i++) {
        const angle = (i / td.perDeploymentPieces) * Math.PI * 2;
        const mp = sim.nav.nearestWalkable(
          vec(targetPos.x + Math.cos(angle) * 2.2, targetPos.y + Math.sin(angle) * 2.2),
        );
        const id = sim.newId();
        sim.state.mines.set(id, {
          id,
          defId: def.subjectId,
          cardId,
          owner: player,
          fieldId,
          pos: mp,
          health: td.pieceHealth,
          built: false,
          buildProgress: 0,
          armDelay: 2,
          triggered: false,
          dead: false,
        });
      }
      break;
    }
  }
}

export function stepCards(sim: Sim): void {
  for (const p of sim.state.players) {
    for (const cid of Object.keys(p.cards)) {
      const cr = p.cards[cid];
      if (cr.cooldownRemaining > 0) cr.cooldownRemaining = Math.max(0, cr.cooldownRemaining - dt);
    }
  }
  // boss assembly sequences arrive
  const pending = sim.state.pendingSummons;
  for (let i = pending.length - 1; i >= 0; i--) {
    const ps = pending[i];
    ps.remaining -= dt;
    if (ps.remaining <= 0) {
      pending.splice(i, 1);
      const squad = sim.spawnSquad(ps.defId, ps.owner, ps.pos);
      sim.pushEvent({ type: 'BossSummoned', player: ps.owner, defId: ps.defId, pos: { ...squad.pos } });
    }
  }
}
